import { useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { PIE_COLORS } from "@/components/DashboardWidgets";
import { useClient } from "@/context/ClientContext";

/*
 * Renderização compartilhada dos dashboards do construtor: a mesma grade e
 * os mesmos widgets aparecem no editor, na pré-visualização e no Dashboard
 * publicado. Os DADOS vêm sempre de POST /dashboard/widgets/data, que aplica
 * o escopo do usuário que está vendo — o frontend não filtra nada.
 */

export const PERIOD_LABELS = { 7: "Últimos 7 dias", 30: "Últimos 30 dias", 90: "Últimos 90 dias", 180: "Últimos 180 dias", 365: "Últimos 12 meses" };
export const WIDTH_CHOICES = [3, 4, 6, 8, 12];

const SPAN = {
  1: "sm:col-span-2 lg:col-span-1", 2: "sm:col-span-2 lg:col-span-2", 3: "sm:col-span-3 lg:col-span-3",
  4: "sm:col-span-3 lg:col-span-4", 5: "sm:col-span-3 lg:col-span-5", 6: "sm:col-span-6 lg:col-span-6",
  7: "sm:col-span-6 lg:col-span-7", 8: "sm:col-span-6 lg:col-span-8", 9: "sm:col-span-6 lg:col-span-9",
  10: "sm:col-span-6 lg:col-span-10", 11: "sm:col-span-6 lg:col-span-11", 12: "sm:col-span-6 lg:col-span-12",
};
const HEIGHT = { 1: "min-h-[128px]", 2: "min-h-[300px]", 3: "min-h-[420px]", 4: "min-h-[540px]" };
const CHART_HEIGHT = { 1: 90, 2: 220, 3: 340, 4: 460 };

export function spanClass(widget) {
  return `col-span-1 ${SPAN[widget.w] || SPAN[6]} ${HEIGHT[widget.h] || HEIGHT[1]}`;
}

export function DashboardGrid({ children, testId }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6 lg:grid-cols-12" data-testid={testId}>
      {children}
    </div>
  );
}

/** Dados de todos os widgets (uma chamada; recarrega quando a definição muda). */
export function useWidgetData(widgets, filters, dashboardId) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const signature = useMemo(
    () => JSON.stringify({ w: (widgets || []).map(({ id, type, config }) => ({ id, type, config })), filters, dashboardId }),
    [widgets, filters, dashboardId],
  );
  const lastRequest = useRef(0);

  useEffect(() => {
    const payload = JSON.parse(signature);
    if (!payload.w.length) {
      setResults({});
      return undefined;
    }
    const request = ++lastRequest.current;
    setLoading(true);
    const timer = setTimeout(() => {
      api.post("/dashboard/widgets/data", { widgets: payload.w, filters: payload.filters || {}, dashboard_id: payload.dashboardId || null })
        .then(({ data }) => request === lastRequest.current && setResults(data.results || {}))
        .catch((err) => {
          if (request !== lastRequest.current) return;
          const message = err.response?.data?.detail || "Não foi possível carregar os dados.";
          setResults(Object.fromEntries(payload.w.map((w) => [w.id, { error: typeof message === "string" ? message : "Erro", code: "REQUEST" }])));
        })
        .finally(() => request === lastRequest.current && setLoading(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [signature]);

  return { results, loading };
}

const fmt = new Intl.NumberFormat("pt-BR");

function Kpi({ data }) {
  const delta = data.delta_pct;
  const up = delta > 0;
  return (
    <div className="flex h-full flex-col justify-end">
      <p className="font-heading text-3xl font-bold tabular-nums">
        {fmt.format(data.value ?? 0)}{data.suffix || ""}
      </p>
      {data.previous !== undefined && (
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          {delta === null || delta === undefined ? (
            <>sem base no período anterior ({fmt.format(data.previous)})</>
          ) : (
            <>
              <span className={`inline-flex items-center font-medium ${up ? "text-amber-600" : "text-emerald-600"}`}>
                {up ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                {Math.abs(delta)}%
              </span>
              vs. período anterior ({fmt.format(data.previous)})
            </>
          )}
        </p>
      )}
      {data.sample !== undefined && <p className="mt-1 text-xs text-muted-foreground">{fmt.format(data.sample)} inspeções</p>}
    </div>
  );
}

const tooltipStyle = { fontSize: 12, borderRadius: 8 };

function Series({ data, config, height }) {
  const points = (data.points || []).map((p) => ({ ...p, label: p.bucket.slice(5).split("-").reverse().join("/") }));
  if (!points.length) return <Empty />;
  const chart = config.chart || "area";
  const common = { data: points, margin: { top: 8, right: 8, left: -18, bottom: 0 } };
  const axes = [
    <CartesianGrid key="g" strokeDasharray="3 3" className="stroke-border" />,
    <XAxis key="x" dataKey="label" tick={{ fontSize: 11 }} />,
    <YAxis key="y" allowDecimals={false} tick={{ fontSize: 11 }} />,
    <Tooltip key="t" contentStyle={tooltipStyle} formatter={(v) => [fmt.format(v), "Ocorrências"]} />,
  ];
  return (
    <div className="flex h-full flex-col">
      {data.previous_total !== undefined && (
        <p className="mb-1 text-xs text-muted-foreground">
          {fmt.format(data.total)} no período · {fmt.format(data.previous_total)} no período anterior
        </p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {chart === "bar" ? (
          <BarChart {...common}>{axes}<Bar isAnimationActive={false} dataKey="value" fill={PIE_COLORS[0]} radius={[4, 4, 0, 0]} /></BarChart>
        ) : chart === "line" ? (
          <LineChart {...common}>{axes}<Line isAnimationActive={false} type="monotone" dataKey="value" stroke={PIE_COLORS[0]} strokeWidth={2} dot={false} /></LineChart>
        ) : (
          <AreaChart {...common}>{axes}<Area isAnimationActive={false} type="monotone" dataKey="value" stroke={PIE_COLORS[0]} fill={PIE_COLORS[0]} fillOpacity={0.15} strokeWidth={2} /></AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function Breakdown({ data, config, height }) {
  const items = (data.items || []).map((i) => ({ name: i.label || i.key, value: i.value }));
  if (!items.length) return <Empty />;
  if (config.chart === "pie") {
    return (
      <div className="flex h-full flex-col gap-2 sm:flex-row sm:items-center">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie isAnimationActive={false} data={items} dataKey="value" nameKey="name" innerRadius="50%" outerRadius="85%" paddingAngle={2}>
              {items.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
        <ul className="min-w-[40%] space-y-1 text-xs">
          {items.map((item, i) => (
            <li key={item.name} className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="truncate">{item.name}</span>
              </span>
              <span className="tabular-nums text-muted-foreground">{fmt.format(item.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={items} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar isAnimationActive={false} dataKey="value" fill={PIE_COLORS[1]} radius={[0, 4, 4, 0]} barSize={14} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Table({ data }) {
  if (!data.rows?.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            {data.columns.map((c) => <th key={c} className="py-1.5 pr-3 font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i} className="border-b border-border/50 last:border-0">
              {row.map((cell, j) => (
                <td key={j} className={`py-1.5 pr-3 ${j > 0 && typeof cell === "number" ? "tabular-nums" : ""}`}>
                  {typeof cell === "string" && /^\d{4}-\d{2}-\d{2}T/.test(cell)
                    ? new Date(cell).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
                    : cell ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty() {
  return <p className="flex h-full items-center justify-center py-6 text-xs text-muted-foreground">Sem dados no período e escopo selecionados.</p>;
}

/** Corpo de um widget a partir do resultado do backend. */
export function WidgetBody({ widget, spec, result, loading }) {
  const config = { ...(spec?.defaults || {}), ...(widget.config || {}) };
  if (widget.type === "nota") {
    return <p className="whitespace-pre-line text-sm text-muted-foreground">{config.text}</p>;
  }
  if (!result) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        {loading ? <Loader2 size={16} className="animate-spin" aria-label="Carregando" /> : "—"}
      </div>
    );
  }
  if (result.error) {
    const Icon = result.code === "MODULE_UNAVAILABLE" || result.code === "QUERY_DENIED" ? Lock : AlertTriangle;
    return (
      <p className="flex h-full items-center justify-center gap-2 text-center text-xs text-muted-foreground" data-testid={`widget-error-${widget.id}`}>
        <Icon size={14} aria-hidden="true" /> {result.error}
      </p>
    );
  }
  const height = CHART_HEIGHT[widget.h] || 220;
  const data = result.data || {};
  switch (spec?.kind) {
    case "kpi": return <Kpi data={data} />;
    case "timeseries": return <Series data={data} config={config} height={height - 20} />;
    case "breakdown": return <Breakdown data={data} config={config} height={height} />;
    case "table": return <Table data={data} />;
    default: return <Empty />;
  }
}

export function WidgetFrame({ widget, spec, children, actions, dragHandle, selected, onSelect, testId, frameRef, style }) {
  return (
    <section
      ref={frameRef}
      style={style}
      className={`${spanClass(widget)} flex flex-col rounded-xl border bg-card p-4 transition-shadow ${
        selected ? "border-primary ring-2 ring-primary/30" : "border-border"
      } ${onSelect ? "cursor-pointer" : ""}`}
      onClick={onSelect}
      aria-label={widget.title || spec?.label}
      data-testid={testId || `widget-${widget.id}`}
    >
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {dragHandle}
          <h3 className="truncate text-sm font-semibold">{widget.title || spec?.label || widget.type}</h3>
        </div>
        {actions}
      </header>
      <div className="min-h-0 flex-1">{children}</div>
    </section>
  );
}

/** Filtros globais do painel (empresa, unidade, área, período). */
export function DashboardFilterBar({ filters, onChange, lockedClientId, testId = "dashboard-filters" }) {
  const { clients } = useClient();
  const [units, setUnits] = useState([]);
  const [areas, setAreas] = useState([]);
  const clientId = lockedClientId || filters.client_id || "";

  useEffect(() => {
    if (!clientId) { setUnits([]); return; }
    api.get("/units", { params: { client_id: clientId } })
      .then(({ data }) => setUnits(Array.isArray(data) ? data : data.items || []))
      .catch(() => setUnits([]));
  }, [clientId]);
  useEffect(() => {
    if (!filters.unit_id) { setAreas([]); return; }
    api.get("/areas", { params: { unit_id: filters.unit_id } })
      .then(({ data }) => setAreas(Array.isArray(data) ? data : data.items || []))
      .catch(() => setAreas([]));
  }, [filters.unit_id]);

  const set = (patch) => onChange({ ...filters, ...patch });
  const select = "h-9 rounded-md border border-border bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <select className={select} value={filters.period_days || 30} onChange={(e) => set({ period_days: Number(e.target.value) })} aria-label="Período" data-testid={`${testId}-period`}>
        {Object.entries(PERIOD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {!lockedClientId && clients.length > 1 && (
        <select className={select} value={filters.client_id || ""} onChange={(e) => set({ client_id: e.target.value || undefined, unit_id: undefined, area_id: undefined })} aria-label="Empresa" data-testid={`${testId}-client`}>
          <option value="">Todas as empresas do meu escopo</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      {units.length > 0 && (
        <select className={select} value={filters.unit_id || ""} onChange={(e) => set({ unit_id: e.target.value || undefined, area_id: undefined })} aria-label="Unidade" data-testid={`${testId}-unit`}>
          <option value="">Todas as unidades</option>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      )}
      {areas.length > 0 && (
        <select className={select} value={filters.area_id || ""} onChange={(e) => set({ area_id: e.target.value || undefined })} aria-label="Área" data-testid={`${testId}-area`}>
          <option value="">Todas as áreas</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      )}
    </div>
  );
}

/** Dashboard publicado (somente leitura) com filtros próprios do visualizador. */
export default function DashboardRenderer({ dashboard, library, testId = "dashboard-render" }) {
  const content = dashboard.published || dashboard.draft || { widgets: [], filters: {} };
  const [filters, setFilters] = useState(content.filters || { period_days: 30 });
  useEffect(() => setFilters(content.filters || { period_days: 30 }), [dashboard.id, content.filters]);
  const { results, loading } = useWidgetData(content.widgets, filters, dashboard.id);
  const specs = useMemo(() => Object.fromEntries((library || []).map((w) => [w.type, w])), [library]);

  return (
    <div className="space-y-4" data-testid={testId}>
      <DashboardFilterBar filters={filters} onChange={setFilters} lockedClientId={dashboard.scope === "tenant" ? dashboard.client_id : null} />
      <DashboardGrid>
        {content.widgets.map((widget) => (
          <WidgetFrame key={widget.id} widget={widget} spec={specs[widget.type]}>
            <WidgetBody widget={widget} spec={specs[widget.type]} result={results[widget.id]} loading={loading} />
          </WidgetFrame>
        ))}
      </DashboardGrid>
    </div>
  );
}
